"use client";

import Image from "next/image";
import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { Menu, X as XIcon } from "lucide-react";
import { useTranslations, useLocale } from 'next-intl';
import { usePathname, useRouter, Link } from '@/i18n/routing';
import { localeNames } from '@/i18n/config';
import { usePrivyWallet } from '@/app/hooks/use-privy-wallet';
import { montserratMedium, montserratSemi } from "../fonts";
import AppButton from "./app-button";
import FizzyButton from "./fizzy-button";

export default function StickyNav() {
  const t = useTranslations('nav');
  const tCommon = useTranslations('common');
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const { authenticated } = usePrivyWallet();
  
  // Start false on both server and client to avoid hydration mismatch; update in effect.
  const [scrolled, setScrolled] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [openSubNav, setOpenSubNav] = useState<string | null>(null);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const languageMenuRef = useRef<HTMLDivElement>(null);
  const goToDownload = () => window.open('/download', '_self');
  
  // Handle language change
  const handleLanguageChange = (newLocale: string) => {
    router.replace(pathname, { locale: newLocale });
    setIsLanguageMenuOpen(false);
    setIsDrawerOpen(false);
  };
  
  // Close language menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (languageMenuRef.current && !languageMenuRef.current.contains(event.target as Node)) {
        setIsLanguageMenuOpen(false);
      }
    };
    
    if (isLanguageMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isLanguageMenuOpen]);
  
  const handleLaunchApp = () => {
    window.open('https://app.xox.exchange/', '_self');
  };

  const navItems = [
    {
      label: t('about'),
      href: '#',
      subItems: [
        { label: t('aboutXox'), href: 'https://docs.xox.exchange/' },
        { label: t('privacyPolicy'), href: 'https://docs.xox.exchange/about/privacy-policy' },
        { label: t('termsOfUse'), href: 'https://docs.xox.exchange/about/terms-of-use' },
      ]
    },
    {
      label: t('community'),
      href: '#',
      subItems: [
        { label: t('discord'), href: 'https://discord.gg/d8kUQq8Z' },
        { label: t('x'), href: 'https://x.com/xox_DEX' },
        { label: t('telegram'), href: 'https://t.me/xoxdex' },
      ]
    },
    {
      label: t('developers'),
      href: '#',
      subItems: [
        { label: t('documentation'), href: 'https://docs.xox.exchange/getting-started' },
        { label: t('apis'), href: 'https://docs.xox.exchange/api-docs' },
      ]
    },
  ];

  useEffect(() => {
    // Check scroll position on mount to handle SSR/hydration
    const checkScroll = () => setScrolled(window.scrollY > 20);
    checkScroll(); // Check immediately
    
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close language menu when scrolled
  useEffect(() => {
    if (scrolled && isLanguageMenuOpen) {
      setIsLanguageMenuOpen(false);
    }
  }, [scrolled, isLanguageMenuOpen]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isDrawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isDrawerOpen]);

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="fixed top-0 left-0 right-0 z-50 flex justify-center pt-6 px-6"
        style={{ willChange: 'transform' }}
      >
        <div className={`
          relative flex items-center justify-between pr-3 pl-6 py-2 rounded-full transition-all duration-500 ease-out
          ${scrolled ? 'w-full max-w-3xl bg-[#050B14]/80 backdrop-blur-xl border border-white/10 shadow-2xl shadow-black/50' : 'w-full max-w-6xl bg-transparent'}
        `}
        style={{ 
          minHeight: '56px', // Reserve space to prevent height shift
          willChange: scrolled ? 'width, background-color' : 'width'
        }}
        >
          {/* Logo XOX */}
          <Link href="/" className="flex items-center gap-2 group z-10">
            <Image
              src="/assets/logo-w-spin.svg"
              alt="XOX logo"
              width={164}
              height={48}
              className="h-9 w-auto"
              priority
            />
          </Link>
          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1 absolute left-1/2 -translate-x-1/2">
            {navItems.map((item, i) => (
              <div
                key={i}
                className="relative"
                onMouseEnter={() => item.subItems && setOpenSubNav(item.label)}
                onMouseLeave={() => setOpenSubNav(null)}
              >
                <Link
                  href={item.href}
                  className={`${montserratMedium.className} px-4 py-2 text-sm font-medium text-white/60 hover:text-white transition-colors hover:bg-white/5 rounded-full flex items-center gap-1`}
                >
                  {item.label}
                  {item.subItems && (
                    <svg
                      className={`w-3 h-3 transition-transform duration-200 ${
                        openSubNav === item.label ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </Link>
                {/* Sub Navigation Dropdown */}
                {item.subItems && openSubNav === item.label && (
                  <div 
                    className="absolute top-full left-0 pt-2 w-48 z-50"
                    onMouseEnter={() => setOpenSubNav(item.label)}
                    onMouseLeave={() => setOpenSubNav(null)}
                  >
                    <div className="bg-[#050B14]/95 backdrop-blur-xl border border-white/10 rounded-lg shadow-2xl py-2">
                      {item.subItems.map((subItem, index) => (
                        <Link
                          key={index}
                          href={subItem.href}
                          onClick={() => setOpenSubNav(null)}
                          target={subItem.href.startsWith('http') ? '_blank' : undefined}
                          rel={subItem.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                          className={`${montserratMedium.className} w-full text-left px-4 py-2 text-sm transition-colors text-white/60 hover:text-white hover:bg-white/5 block`}
                        >
                          {subItem.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Right Actions */}
          <div className="hidden md:flex items-center gap-2 z-10 relative">
            {/* Language Switcher - Hide when scrolled */}
            {!scrolled && (
              <div className="relative" ref={languageMenuRef}>
                <button
                  onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
                  className="flex items-center gap-2 px-3 py-2 rounded-full text-white/60 hover:text-white transition-colors hover:bg-white/5"
                  aria-label={t('language')}
                  aria-expanded={isLanguageMenuOpen}
                >
                  <Image
                    src="/icons/icon_language.svg"
                    alt={t('language')}
                    width={16}
                    height={16}
                    className="w-4 h-4"
                  />
                  <svg
                    className={`w-3 h-3 transition-transform duration-200 ${
                      isLanguageMenuOpen ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {/* Language Dropdown */}
                {isLanguageMenuOpen && (
                  <div className="absolute top-full right-0 mt-2 w-40 bg-[#050B14]/95 backdrop-blur-xl border border-white/10 rounded-lg shadow-2xl py-2 z-50">
                    {Object.entries(localeNames).map(([code, name]) => (
                      <button
                        key={code}
                        onClick={() => handleLanguageChange(code)}
                        className={`${montserratMedium.className} w-full text-left px-4 py-2 text-sm transition-colors ${
                          locale === code
                            ? 'text-white bg-white/10'
                            : 'text-white/60 hover:text-white hover:bg-white/5'
                        }`}
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {authenticated ? (
              <FizzyButton href="/earn" text={t('portfolio')} />
            ) : (
              <FizzyButton 
                onClick={handleLaunchApp}
                text={t('launch')}
              />
            )}
          </div>

          {/* Mobile Toggle */}
          <button 
            className="md:hidden p-2 text-white z-10"
            aria-label={t('toggleNavigation')}
            onClick={() => setIsDrawerOpen(!isDrawerOpen)}
          >
            {isDrawerOpen ? <XIcon className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </motion.nav>

      {/* Mobile Drawer - Keep existing drawer content */}
      {isDrawerOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-40 md:hidden bg-[color:var(--card-surface)] flex flex-col"
        >
          <div className="flex flex-col h-full overflow-y-auto">
            <div className="flex flex-col p-12">


            {/* Navigation Items - mirror desktop structure */}
            <nav className="flex flex-col gap-2 mt-18">
              {navItems.map((item, i) => {
                const isOpen = openSubNav === item.label;
                return (
                  <motion.div
                    key={i}
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <div className="border-b border-[color:var(--field-border)] pb-2">
                      <button
                        onClick={() => setOpenSubNav(isOpen ? null : item.label)}
                        className={`${montserratMedium.className} w-full flex items-center justify-between text-left text-lg cursor-pointer transition-colors py-2 text-[color:var(--text-primary)] hover:text-[color:var(--main-color)]`}
                        aria-expanded={isOpen}
                      >
                        <span>{item.label}</span>
                        <svg
                          className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {isOpen && (
                        <div className="mt-1 flex flex-col gap-1">
                          {item.subItems.map((subItem) => (
                            <Link
                              key={subItem.label}
                              href={subItem.href}
                              onClick={() => setIsDrawerOpen(false)}
                              target={subItem.href.startsWith('http') ? '_blank' : undefined}
                              rel={subItem.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                              className={`${montserratMedium.className} block pl-4 pr-2 py-1.5 text-base text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] transition-colors`}
                            >
                              {subItem.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}

              {/* Language Switcher */}
              <motion.div
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.35 }}
              >
                <div className="border-b border-[color:var(--field-border)] pb-2">
                  {(() => {
                    const languageLabel = t('language');
                    const isLanguageOpen = openSubNav === languageLabel;
                    return (
                      <>
                        <button
                          onClick={() => setOpenSubNav(isLanguageOpen ? null : languageLabel)}
                          className={`${montserratMedium.className} w-full flex items-center justify-between text-left text-lg cursor-pointer transition-colors py-2 text-[color:var(--text-primary)] hover:text-[color:var(--main-color)]`}
                          aria-expanded={isLanguageOpen}
                          style={{ cursor: 'pointer' } as React.CSSProperties}
                        >
                          <div className="flex items-center gap-2">
                            <Image
                              src="/icons/icon_language.svg"
                              alt={languageLabel}
                              width={16}
                              height={16}
                              className="w-4 h-4"
                            />
                            <span>{languageLabel}</span>
                          </div>
                          <svg
                            className={`w-4 h-4 transition-transform duration-200 ${isLanguageOpen ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        {isLanguageOpen && (
                          <div className="mt-1 flex flex-col gap-1">
                            {Object.entries(localeNames).map(([code, name]) => (
                              <button
                                key={code}
                                onClick={() => handleLanguageChange(code)}
                                className={`${montserratMedium.className} block cursor-pointer pl-4 pr-2 py-1.5 text-base transition-colors text-left ${locale === code ? 'text-[color:var(--main-color)]' : 'text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]'}`}
                                style={{ cursor: 'pointer' } as React.CSSProperties}
                              >
                                {name}
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </motion.div>
              {/* Launch App / Account Button */}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="mt-8 pt-2"
              >
                {authenticated ? (
                  <AppButton
                    href="/earn"
                    text={t('portfolio')}
                    variant="outline"
                    size="sm"
                  />
                ) : (
                  <AppButton
                    text={tCommon('launchApp')}
                    variant="outline"
                    size="sm"
                    onClick={handleLaunchApp}
                  />
                )}
              </motion.div>
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="mt-2 pt-2"
              >
                <AppButton
                  text={tCommon('download')}
                  icon="/icons/mobile-w.svg"
                  variant="outline"
                  size="sm"
                  onClick={goToDownload}
                />
              </motion.div>
            </nav>
            </div>
          </div>
        </motion.div>
      )}
    </>
  );
}

